import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Droplet, Heart, ArrowRight, Gift } from 'lucide-react';

const HomePage = () => {
  const collections = [
    {
      id: 'iconiques',
      icon: <Heart size={48} />,
      title: 'Les Iconiques',
      subtitle: 'Fragrances intemporelles',
      description:
        'Des parfums classiques et élégants qui traversent les époques. Des créations intemporelles qui séduisent génération après génération.',
      prices: '10ml/10€, 30ml/17€, 50ml/25€, 100ml/40€',
      color: '#5C2018',
    },
    {
      id: 'pepites',
      icon: <Droplet size={48} />,
      title: 'Les Pépites',
      subtitle: 'Fragrances rares & accessibles',
      description:
        'Des senteurs précieuses et rares, dénichées aux quatre coins du monde. Le luxe accessible pour tous les amateurs de beauté olfactive.',
      prices: 'Échantillon 2€, 10ml/15€, 50ml/39€',
      color: '#1E7A6A',
    },
    {
      id: 'audacieux',
      icon: <Sparkles size={48} />,
      title: 'Les Audacieux',
      subtitle: 'Senteurs uniques & avant-gardistes',
      description:
        'Pour les âmes aventureuses qui osent sortir des sentiers battus. Des fragrances audacieuses qui ne laissent personne indifférent.',
      prices: 'Échantillon 2€, 10ml/25€, 50ml/59€',
      color: '#B8922A',
    },
  ];

  return (
    <div className="min-h-screen bg-[#FDFAF4]">
      {/* Hero Section with Video Background */}
      <section className="relative min-h-[92vh] flex items-center py-20 overflow-hidden bg-[#332724]" data-testid="hero-section">
        {/* Background video */}
        <video
          src="/assets/boutique-visite-virtuelle.mp4"
          poster="/assets/boutique_3.jpg"
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover z-0"
          data-testid="hero-video"
        />
        {/* Dark overlay for readability - using brand colors only */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#332724]/90 via-[#332724]/70 to-[#5C2018]/50 z-10" />

        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-20 w-full">
          {/* Left - Text */}
          <div className="text-center lg:text-left text-[#FDFAF4]">
            <img
              src="/assets/logo-harmony.jpg"
              alt="Harmony Parfumerie"
              className="h-24 w-24 mx-auto lg:mx-0 mb-6 rounded-full object-cover shadow-[0_10px_40px_rgba(0,0,0,0.35)] ring-2 ring-[#B8922A]/50"
            />
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
              Bar à parfums • Concept store • Polygone de Béziers
            </p>
            <h1
              className="text-5xl md:text-6xl lg:text-7xl font-light tracking-tight mb-6 text-[#FDFAF4]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Le parfum,
              <br />
              autrement.
            </h1>
            <p className="text-lg md:text-xl font-light text-[#FDFAF4]/90 mb-8 leading-relaxed">
              Un lieu d'échange, de curiosité et de découverte où les parfums sont racontés,
              partagés, vécus.
            </p>
            <div className="flex gap-4 justify-center lg:justify-start flex-wrap">
              <Link
                to="/boutique"
                className="btn-primary px-8 py-3 rounded-full font-medium uppercase tracking-[0.2em] text-sm"
                data-testid="cta-boutique-hero"
              >
                Découvrir la boutique
              </Link>
              <Link
                to="/personnalisation"
                className="bg-[#FDFAF4]/10 hover:bg-[#FDFAF4]/20 backdrop-blur-md border border-[#FDFAF4]/40 text-[#FDFAF4] px-8 py-3 rounded-full font-medium uppercase tracking-[0.2em] text-sm transition-colors"
                data-testid="cta-personnalisation"
              >
                Diagnostic olfactif
              </Link>
            </div>
          </div>

          {/* Right - badge floating */}
          <div className="relative hidden lg:flex justify-end">
            <div className="bg-[#FDFAF4]/10 backdrop-blur-md border border-[#FDFAF4]/30 text-[#FDFAF4] px-8 py-6 rounded-2xl shadow-2xl max-w-xs">
              <p
                className="text-2xl italic mb-2"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Venez nous rencontrer
              </p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#FDFAF4] mb-3">
                3 Carrefour de l'Hours · Niveau Écluses
              </p>
              <p className="text-sm font-light text-[#FDFAF4]/85">
                Lundi — Samedi · 10h à 20h
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CONCEPT & PARCOURS D'ACHAT */}
      <section className="py-20 px-6 bg-[#FDFAF4] border-y border-[#B8922A]/15" data-testid="home-concept-parcours">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-3">Le concept Harmony</p>
            <h2
              className="text-4xl md:text-5xl font-light mb-5 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Acheter chez nous, c'est simple
            </h2>
            <p className="text-base md:text-lg text-[#332724] font-light leading-relaxed max-w-3xl mx-auto">
              Harmony n'est pas une parfumerie classique. <strong>Vous commandez en ligne, vous récupérez en boutique</strong> (ou en livraison), et <em>si vous le souhaitez</em>, vous bénéficiez d'une consultation 15 min offerte pour découvrir LE parfum qui vous ressemble vraiment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10 max-w-5xl mx-auto">
            {[
              { num: '1', title: 'Je commande', text: 'En 2 clics depuis la boutique en ligne. Paiement CB sécurisé.' },
              { num: '2', title: 'Je récupère', text: 'En boutique (Click & Collect, gratuit) ou en livraison France (8,90€, offerte dès 60€).' },
              { num: '3', title: 'Je profite', text: 'D\'une consultation 15 min offerte avec nos experts — si je veux. Sinon, je repars simplement avec mon flacon.' },
            ].map((s) => (
              <div key={s.num} className="bg-white p-6 rounded-2xl border border-gold flex items-start gap-4" data-testid={`home-step-${s.num}`}>
                <div className="w-12 h-12 bg-[#5C2018] text-[#FDFAF4] rounded-full flex items-center justify-center flex-shrink-0 text-xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                  {s.num}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#5C2018] uppercase tracking-[0.15em] mb-2">{s.title}</p>
                  <p className="text-sm text-[#332724] font-light leading-relaxed">{s.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-[#5C2018] to-[#2E120D] text-[#FDFAF4] rounded-2xl p-8 md:p-10 text-center relative overflow-hidden">
            <img
              src="/assets/rose-filigrane.jpg"
              alt=""
              aria-hidden="true"
              className="absolute -top-10 -right-10 w-[280px] h-[280px] object-contain pointer-events-none select-none opacity-[0.08]"
            />
            <div className="relative z-10">
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-3">Notre promesse</p>
              <h3 className="text-3xl md:text-4xl font-light mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                Le luxe du temps, l'art de la rencontre
              </h3>
              <p className="text-base font-light leading-relaxed max-w-2xl mx-auto mb-6 opacity-95">
                Vous payez l'expérience, vous repartez avec votre signature.
                <br />
                <span className="text-sm opacity-80">
                  Rituels Parfum, abonnement mensuel, recharges, échantillons, bons cadeaux — tout est dans notre boutique en ligne.
                </span>
              </p>
              <Link
                to="/boutique"
                className="inline-flex items-center gap-3 bg-[#B8922A] hover:bg-[#9a7922] text-[#FDFAF4] px-8 py-3.5 rounded-full text-xs uppercase tracking-[0.2em] transition-colors"
                data-testid="home-boutique-cta"
              >
                Découvrir la boutique
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Collections Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <h2
            className="text-4xl md:text-5xl font-light text-center mb-4 text-[#5C2018]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            Nos Collections
          </h2>
          <p className="text-center text-[#332724] font-light mb-16 max-w-2xl mx-auto">
            Trois univers olfactifs distincts pour exprimer toutes les facettes de votre
            personnalité.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {collections.map((collection) => (
              <div
                key={collection.id}
                className="perfume-card bg-white p-8 rounded-lg border border-gold shadow-[0_8px_32px_rgba(92, 32, 24,0.05)]"
                data-testid={`collection-${collection.id}`}
              >
                <div className="mb-6" style={{ color: collection.color }}>
                  {collection.icon}
                </div>
                <h3
                  className="text-3xl mb-2"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontStyle: 'italic',
                    color: collection.color,
                  }}
                >
                  {collection.title}
                </h3>
                <p
                  className="text-sm uppercase tracking-[0.2em] mb-4"
                  style={{ color: collection.color }}
                >
                  {collection.subtitle}
                </p>
                <p className="text-[#332724] font-light mb-6 leading-relaxed">
                  {collection.description}
                </p>
                <p className="text-sm text-[#B8922A] font-medium mb-6">{collection.prices}</p>
                <Link
                  to={`/boutique/fragrances`}
                  className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] hover:gap-4 transition-all"
                  style={{ color: collection.color }}
                  data-testid={`explore-${collection.id}`}
                >
                  Explorer <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Notre Concept Store */}
      <section className="py-24 px-6 bg-white" data-testid="concept-store-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
              Bien plus qu'une parfumerie
            </p>
            <h2
              className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Notre concept store
            </h2>
            <p className="text-lg text-[#332724] font-light leading-relaxed max-w-3xl mx-auto">
              Harmony, c'est un véritable <strong>bar à parfums concept store</strong> où
              chaque visite devient une expérience. On y vient pour découvrir, sentir,
              composer — mais aussi pour <em>flâner</em>, offrir, et se laisser séduire par
              un univers complet pensé autour des sens.
            </p>
          </div>

          {/* Grid : 4 piliers du concept store */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <div className="bg-[#FDFAF4] rounded-2xl p-6 border border-gold" data-testid="concept-pilier-parfums">
              <div className="w-14 h-14 bg-[#5C2018]/10 rounded-full flex items-center justify-center mb-4">
                <Droplet className="text-[#5C2018]" size={26} />
              </div>
              <h3
                className="text-2xl mb-2 text-[#5C2018]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Bar à parfums
              </h3>
              <p className="text-sm text-[#332724] font-light leading-relaxed">
                3 collections, 4 formats, recharges éco-responsables et personnalisation
                sur-mesure.
              </p>
            </div>
            <div className="bg-[#FDFAF4] rounded-2xl p-6 border border-gold" data-testid="concept-pilier-coffrets">
              <div className="w-14 h-14 bg-[#1E7A6A]/10 rounded-full flex items-center justify-center mb-4">
                <Sparkles className="text-[#1E7A6A]" size={26} />
              </div>
              <h3
                className="text-2xl mb-2 text-[#5C2018]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Coffrets cadeaux
              </h3>
              <p className="text-sm text-[#332724] font-light leading-relaxed">
                Composés à la main, à partir de 15€. Pour soi, pour offrir, pour les grandes
                occasions.
              </p>
            </div>
            <div className="bg-[#FDFAF4] rounded-2xl p-6 border border-gold" data-testid="concept-pilier-ambiance">
              <div className="w-14 h-14 bg-[#B8922A]/10 rounded-full flex items-center justify-center mb-4">
                <Heart className="text-[#B8922A]" size={26} />
              </div>
              <h3
                className="text-2xl mb-2 text-[#5C2018]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Bougies & brumes
              </h3>
              <p className="text-sm text-[#332724] font-light leading-relaxed">
                Une sélection raffinée pour parfumer la maison, l'instant, l'humeur.
              </p>
            </div>
            <div className="bg-[#FDFAF4] rounded-2xl p-6 border border-gold" data-testid="concept-pilier-accessoires">
              <div className="w-14 h-14 bg-[#5C2018]/10 rounded-full flex items-center justify-center mb-4">
                <ArrowRight className="text-[#5C2018]" size={26} />
              </div>
              <h3
                className="text-2xl mb-2 text-[#5C2018]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Accessoires
              </h3>
              <p className="text-sm text-[#332724] font-light leading-relaxed">
                Vaporisateurs de sac, cartes cadeaux, écrins… le parfum se prolonge dans chaque
                détail.
              </p>
            </div>
          </div>

          {/* Mosaic visuelle du concept */}
          <div className="grid grid-cols-12 gap-4 max-w-6xl mx-auto">
            <div className="col-span-12 md:col-span-7 row-span-2">
              <img
                src="/assets/boutique_1.jpg"
                alt="Bar à parfums Harmony - intérieur"
                className="w-full h-full object-cover rounded-2xl shadow-[0_15px_50px_rgba(92, 32, 24,0.15)] aspect-[16/11] md:aspect-auto"
                data-testid="concept-img-1"
              />
            </div>
            <div className="col-span-6 md:col-span-5">
              <img
                src="/assets/coffrets.jpg"
                alt="Coffrets cadeaux Harmony"
                className="w-full h-full object-cover rounded-2xl shadow-[0_15px_50px_rgba(92, 32, 24,0.10)] aspect-square"
                data-testid="concept-img-2"
              />
            </div>
            <div className="col-span-6 md:col-span-5">
              <img
                src="/assets/gallery/brumes_bougies.jpg"
                alt="Brumes et bougies Harmony"
                className="w-full h-full object-cover rounded-2xl shadow-[0_15px_50px_rgba(92, 32, 24,0.10)] aspect-square"
                data-testid="concept-img-3"
              />
            </div>
          </div>

          {/* Citation centrale */}
          <div className="text-center mt-16 max-w-3xl mx-auto">
            <p
              className="text-2xl md:text-3xl text-[#1E7A6A] italic leading-relaxed"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              « Un lieu où l'on entre pour un parfum,
              <br />et où l'on repart avec une histoire. »
            </p>
            <Link
              to="/qui-sommes-nous"
              className="inline-flex items-center gap-2 mt-8 text-sm uppercase tracking-[0.2em] text-[#5C2018] hover:gap-4 transition-all"
              data-testid="concept-cta"
            >
              Découvrir notre univers <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Ils nous font confiance */}
      <section className="py-24 px-6 bg-[#FDFAF4]" data-testid="confiance-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4 text-center">
            Partenariats & collaborations
          </p>
          <h2
            className="text-4xl md:text-5xl font-light text-center mb-4 text-[#5C2018]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            Ils nous font confiance
          </h2>
          <p className="text-center text-[#332724] font-light mb-16 max-w-2xl mx-auto">
            Des partenaires institutionnels et des entreprises qui nous ont choisis pour
            partager notre savoir-faire olfactif.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
            {/* Visit Insolite - vraie collaboration */}
            <div
              className="bg-white rounded-2xl border border-gold p-8 flex flex-col"
              data-testid="partner-visitinsolite"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#1E7A6A] mb-2">
                Partenariat
              </p>
              <h3
                className="text-3xl text-[#5C2018] leading-tight mb-4"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Visit'insolite
                <br />Montpellier
              </h3>
              <p className="text-[#332724] font-light leading-relaxed mb-4">
                Nous collaborons régulièrement avec <strong>Pauline Salembier</strong>, guide
                conférencière de la région Occitanie. Dans le cadre de ses visites insolites
                dédiées à l'<em>artisanat d'art</em>, nous accueillons ses groupes pour leur
                faire vivre une <strong>expérience olfactive sur-mesure</strong> au cœur de
                notre concept store.
              </p>
              <a
                href="https://www.visitinsolite.com/un-metier-passion/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#1E7A6A] uppercase tracking-[0.2em] mt-auto hover:text-[#5C2018] transition-colors inline-flex items-center gap-2"
              >
                Découvrir Visit'insolite <ArrowRight size={14} />
              </a>
            </div>

            {/* Animations entreprise */}
            <div
              className="bg-[#5C2018] text-[#FDFAF4] rounded-2xl p-8 flex flex-col"
              data-testid="partner-entreprises"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-2">
                Séminaires & groupes
              </p>
              <h3
                className="text-3xl mb-4 leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Ateliers DIY pour
                <br />les entreprises
              </h3>
              <p className="font-light leading-relaxed mb-6">
                J'anime régulièrement des <strong>ateliers DIY olfactifs</strong> pour les
                <strong> groupes</strong>, <strong>séminaires d'entreprise</strong> et
                team-buildings. Une parenthèse sensorielle, fédératrice et inattendue — chaque
                participant repart avec sa propre création parfumée.
              </p>
              <Link
                to="/evenements"
                className="inline-block bg-[#B8922A] hover:bg-[#9a7922] text-[#FDFAF4] px-6 py-3 rounded-full font-medium uppercase tracking-[0.2em] text-xs transition-colors self-start mt-auto"
                data-testid="cta-ateliers-entreprise"
              >
                Demander un devis
              </Link>
            </div>
          </div>

          {/* Photo du bar à parfum ambulant - ambiance mariage outdoor */}
          <div className="mt-12 relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(92, 32, 24,0.15)]">
            <img
              src="/assets/bar-ambulant-mariage.jpg"
              alt="Bar à parfums ambulant Harmony — mariage et événements en extérieur"
              className="w-full h-auto object-cover aspect-[21/9]"
              data-testid="bar-ambulant-photo"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#332724]/90 to-transparent p-6 md:p-8">
              <p className="text-[#FDFAF4] text-lg md:text-2xl font-light italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                « Notre bar à parfums se déplace pour vos plus beaux moments. »
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section dédiée Partenariats & Collaborations */}
      <section className="py-24 px-6 bg-[#332724] text-[#FDFAF4]" data-testid="partenariats-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
                Vous êtes une entreprise ?
              </p>
              <h2
                className="text-4xl md:text-5xl font-light mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Partenariats &
                <br />collaborations
                <br />d'entreprise
              </h2>
              <p className="text-lg font-light leading-relaxed mb-6 text-[#FDFAF4]/90">
                Hôtels, restaurants, agences événementielles, marques, comités d'entreprise,
                cabinets d'avocats, instituts… Nous construisons avec vous une
                <strong> collaboration sur-mesure</strong> autour du parfum.
              </p>
              <ul className="space-y-3 mb-8 text-[#FDFAF4]/85">
                <li className="flex gap-3">
                  <span className="text-[#B8922A] flex-shrink-0">◆</span>
                  <span>Cadeaux corporate & coffrets personnalisés à votre marque</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#B8922A] flex-shrink-0">◆</span>
                  <span>Ateliers DIY pour vos séminaires, team-buildings et incentives</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#B8922A] flex-shrink-0">◆</span>
                  <span>Bar à parfums ambulant pour vos inaugurations & salons</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#B8922A] flex-shrink-0">◆</span>
                  <span>Co-branding, création de fragrance signature pour votre marque</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#B8922A] flex-shrink-0">◆</span>
                  <span>Partenariats hôteliers, conciergeries, événementiel haut de gamme</span>
                </li>
              </ul>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/evenements"
                  className="inline-block bg-[#B8922A] hover:bg-[#9a7922] text-[#FDFAF4] px-8 py-3 rounded-full font-medium uppercase tracking-[0.2em] text-sm transition-colors"
                  data-testid="cta-partenariat"
                >
                  Faire une demande
                </Link>
                <a
                  href="/assets/plaquette-harmony-entreprises.pdf"
                  download="Plaquette-Harmony-Entreprises.pdf"
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-[#FDFAF4]/10 border border-[#B8922A] hover:bg-[#B8922A]/20 text-[#FDFAF4] uppercase tracking-[0.2em] text-sm transition-colors"
                  data-testid="cta-telecharger-plaquette"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Télécharger la plaquette
                </a>
                <a
                  href="mailto:contact@maisonharmony.com?subject=Demande%20de%20partenariat%20-%20Harmony%20Parfumerie"
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-full border border-[#FDFAF4]/30 hover:border-[#B8922A] text-[#FDFAF4] uppercase tracking-[0.2em] text-sm transition-colors"
                  data-testid="cta-partenariat-email"
                >
                  Nous écrire
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#FDFAF4]/5 backdrop-blur-sm border border-[#FDFAF4]/15 rounded-2xl p-6 flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-[#B8922A]/20 rounded-full flex items-center justify-center mb-3">
                  <span className="text-[#B8922A] text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>H</span>
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">Hôtellerie</p>
                <p className="text-sm text-[#FDFAF4]/80 font-light">Welcome gift, signature olfactive lobby</p>
              </div>
              <div className="bg-[#FDFAF4]/5 backdrop-blur-sm border border-[#FDFAF4]/15 rounded-2xl p-6 flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-[#1E7A6A]/20 rounded-full flex items-center justify-center mb-3">
                  <span className="text-[#1E7A6A] text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>É</span>
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#1E7A6A] mb-1">Événementiel</p>
                <p className="text-sm text-[#FDFAF4]/80 font-light">Privatisation & animations clés en main</p>
              </div>
              <div className="bg-[#FDFAF4]/5 backdrop-blur-sm border border-[#FDFAF4]/15 rounded-2xl p-6 flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-[#5C2018]/40 rounded-full flex items-center justify-center mb-3">
                  <span className="text-[#FDFAF4] text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>C</span>
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">CSE & corporate</p>
                <p className="text-sm text-[#FDFAF4]/80 font-light">Cadeaux d'affaires, fin d'année, fidélisation</p>
              </div>
              <div className="bg-[#FDFAF4]/5 backdrop-blur-sm border border-[#FDFAF4]/15 rounded-2xl p-6 flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-[#B8922A]/20 rounded-full flex items-center justify-center mb-3">
                  <span className="text-[#B8922A] text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>M</span>
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">Marques & créateurs</p>
                <p className="text-sm text-[#FDFAF4]/80 font-light">Co-branding & fragrance sur-mesure</p>
              </div>
            </div>
          </div>

          <p className="text-center text-[#FDFAF4]/60 text-sm italic mt-12 max-w-2xl mx-auto">
            « Chaque partenariat est unique. Nous prenons le temps d'écouter, de comprendre,
            et de composer la collaboration qui vous ressemble. »
          </p>
        </div>
      </section>

      {/* Coffrets Section */}
      <section className="py-24 px-6 bg-[#FDFAF4]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
              L'expérience Harmony
            </p>
            <h2
              className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Tout se personnalise,
              <br />
              y compris les coffrets.
            </h2>
            <p className="text-lg text-[#332724] font-light leading-relaxed mb-6">
              Chez Harmony, rien n'est standard. Nous composons avec vous une expérience
              olfactive sur-mesure : le choix des fragrances, leur intensité, l'écrin qui les
              accueille. Chaque coffret est unique, pensé pour celle ou celui à qui vous
              l'offrirez.
            </p>
            <p
              className="text-xl text-[#1E7A6A] mb-8 italic"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              « Un cadeau ne devrait jamais ressembler à un autre. »
            </p>
            <Link
              to="/boutique/coffret"
              className="inline-block btn-primary px-8 py-3 rounded-full font-medium uppercase tracking-[0.2em] text-sm"
              data-testid="cta-coffrets"
            >
              Composer mon coffret
            </Link>
          </div>
          <div className="order-1 lg:order-2">
            <img
              src="https://images.pexels.com/photos/1666065/pexels-photo-1666065.jpeg?auto=compress&cs=tinysrgb&w=1200&q=85"
              alt="Coffret sur-mesure Harmony"
              className="w-full h-auto rounded-2xl shadow-[0_20px_60px_rgba(92, 32, 24,0.15)] object-cover aspect-[4/3]"
            />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2
            className="text-4xl md:text-5xl font-light text-center mb-4 text-[#5C2018]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            Nos Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            <Link
              to="/personnalisation"
              className="perfume-card text-center bg-[#FDFAF4] p-8 rounded-lg border border-gold shadow-[0_8px_32px_rgba(92, 32, 24,0.05)] block"
              data-testid="service-diagnostic"
            >
              <div className="w-16 h-16 bg-[#1E7A6A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Droplet className="text-[#1E7A6A]" size={32} />
              </div>
              <h3 className="text-xl font-medium mb-2 text-[#332724]">Diagnostic Olfactif</h3>
              <p className="text-[#332724] font-light leading-relaxed mb-4">
                Un accompagnement personnalisé pour découvrir les fragrances qui vous
                correspondent.
              </p>
              <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#1E7A6A]">
                Découvrir <ArrowRight size={16} />
              </span>
            </Link>
            <Link
              to="/rendez-vous"
              className="perfume-card text-center bg-[#FDFAF4] p-8 rounded-lg border border-gold shadow-[0_8px_32px_rgba(92, 32, 24,0.05)] block"
              data-testid="service-personnalisation"
            >
              <div className="w-16 h-16 bg-[#B8922A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="text-[#B8922A]" size={32} />
              </div>
              <h3 className="text-xl font-medium mb-2 text-[#332724]">
                Personnalisation Sur-Mesure
              </h3>
              <p className="text-[#332724] font-light leading-relaxed mb-4">
                Créez votre fragrance unique avec notre service de personnalisation (+5€).
              </p>
              <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#B8922A]">
                Réserver <ArrowRight size={16} />
              </span>
            </Link>
            <Link
              to="/boutique/abonnement"
              className="perfume-card text-center bg-[#FDFAF4] p-8 rounded-lg border border-gold shadow-[0_8px_32px_rgba(92, 32, 24,0.05)] block"
              data-testid="service-refill"
            >
              <div className="w-16 h-16 bg-[#5C2018]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="text-[#5C2018]" size={32} />
              </div>
              <h3 className="text-xl font-medium mb-2 text-[#332724]">
                Recharges Éco-responsables
              </h3>
              <p className="text-[#332724] font-light leading-relaxed mb-4">
                Tous nos parfums sont rechargeables pour un geste en faveur de l'environnement.
              </p>
              <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#5C2018]">
                S'abonner <ArrowRight size={16} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Accessoires accessibles */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src="/assets/gallery/vaporisateur_sac.jpg"
              alt="Vaporisateurs de sac Harmony"
              className="w-full h-auto rounded-2xl shadow-[0_20px_60px_rgba(92, 32, 24,0.15)] object-cover max-w-md mx-auto"
            />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
              Accessoires
            </p>
            <h2
              className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Vaporisateur
              <br />de sac
            </h2>
            <p className="text-lg text-[#332724] font-light leading-relaxed mb-6">
              Emportez votre fragrance partout avec vous. Notre vaporisateur de sac rechargeable
              est l'allié indispensable de votre quotidien — élégant, pratique, disponible en
              5 coloris.
            </p>
            <p
              className="text-3xl text-[#1E7A6A] mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Seulement 5€
            </p>
            <Link
              to="/contact"
              className="inline-block btn-secondary px-8 py-3 rounded-full font-medium uppercase tracking-[0.2em] text-sm"
              data-testid="cta-vaporisateur"
            >
              En boutique
            </Link>
          </div>
        </div>
      </section>

      {/* Carte cadeau */}
      <section className="py-20 px-6 bg-[#FDFAF4]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
              Le cadeau qui plaît à coup sûr
            </p>
            <h2
              className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              La carte cadeau
              <br />Harmony
            </h2>
            <p className="text-lg text-[#332724] font-light leading-relaxed mb-6">
              Offrez la liberté de choisir. Notre carte cadeau permet à votre proche de
              découvrir nos fragrances, de composer son coffret sur-mesure ou de profiter de
              notre service de personnalisation.
            </p>
            <p className="text-lg text-[#332724] font-light leading-relaxed mb-8">
              Disponible <strong className="text-[#5C2018]">au montant de votre choix</strong>,
              dans un écrin élégant signé Harmony.
            </p>
            <Link
              to="/boutique/bons-cadeaux"
              className="inline-block btn-primary px-8 py-3 rounded-full font-medium uppercase tracking-[0.2em] text-sm"
              data-testid="cta-carte-cadeau"
            >
              Offrir une carte cadeau
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img
              src="/assets/gallery/bon_cadeau.jpg"
              alt="Carte cadeau Harmony"
              className="w-full h-auto rounded-2xl shadow-[0_20px_60px_rgba(92, 32, 24,0.15)] object-cover"
            />
            <img
              src="/assets/gallery/card_congrats.jpg"
              alt="Carte de félicitations Harmony"
              className="w-full h-auto rounded-2xl shadow-[0_20px_60px_rgba(92, 32, 24,0.15)] object-cover mt-8"
            />
          </div>
        </div>
      </section>

      {/* Gallery ambiance */}
      <section className="py-24 px-6 bg-[#FDFAF4]">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4 text-center">
            Notre univers
          </p>
          <h2
            className="text-4xl md:text-5xl font-light text-center mb-4 text-[#5C2018]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            L'élégance attire,
            <br />
            le parfum ensorcelle.
          </h2>
          <p className="text-center text-[#332724] font-light mb-16 max-w-2xl mx-auto italic text-lg">
            « Un sniff... puis impossible de s'arrêter. »
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <img
              src="/assets/gallery/roses.jpg"
              alt="Roses parfumées"
              className="w-full h-64 object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow"
            />
            <img
              src="/assets/gallery/brumes_bougies.jpg"
              alt="Brumes et bougies Harmony"
              className="w-full h-64 object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow"
            />
            <img
              src="/assets/coffrets.jpg"
              alt="Box cadeau Harmony"
              className="w-full h-64 object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow"
            />
          </div>
        </div>
      </section>

      {/* Offrir Harmony - Bons cadeaux */}
      <section className="py-20 px-6 bg-[#FDFAF4] relative overflow-hidden" data-testid="offrir-harmony-section">
        <img
          src="/assets/rose-filigrane.jpg"
          alt=""
          aria-hidden="true"
          className="absolute -top-20 -right-20 w-[500px] h-[500px] object-contain pointer-events-none select-none mix-blend-multiply opacity-[0.06]"
        />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center bg-white rounded-2xl border border-gold shadow-[0_15px_50px_rgba(92, 32, 24,0.08)] overflow-hidden">
            <div className="p-10 md:p-12">
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4 flex items-center gap-2">
                <Gift size={14} /> Idée cadeau
              </p>
              <h2
                className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Offrir Harmony
              </h2>
              <p className="text-lg text-[#332724] font-light leading-relaxed mb-6">
                Offrez un <strong>moment Harmony</strong> : un parfum sur-mesure, un coffret
                découverte ou l'expérience d'un diagnostic olfactif personnalisé.
              </p>
              <ul className="space-y-2 mb-8 text-[#332724] font-light">
                <li className="flex gap-3"><span className="text-[#B8922A]">◆</span> Bons cadeaux digitaux dès 10€</li>
                <li className="flex gap-3"><span className="text-[#B8922A]">◆</span> Valables 1 an, utilisables en boutique ou à distance</li>
                <li className="flex gap-3"><span className="text-[#B8922A]">◆</span> Envoyés directement au bénéficiaire avec votre message</li>
                <li className="flex gap-3"><span className="text-[#B8922A]">◆</span> Paiement CB sécurisé</li>
              </ul>
              <Link
                to="/boutique"
                className="inline-block btn-primary px-8 py-3 rounded-full text-sm uppercase tracking-[0.2em]"
                data-testid="cta-offrir-boutique"
              >
                Offrir un bon cadeau →
              </Link>
            </div>
            <div className="relative h-full min-h-[300px] hidden lg:block">
              <img
                src="/assets/coffrets.jpg"
                alt="Bon cadeau Harmony"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-white/40" />
            </div>
          </div>

          {/* Mini CTAs occasions */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8 text-center">
            {[
              'Saint-Valentin', 'Fête des mères', 'Anniversaire', 'Noël',
            ].map((occ) => (
              <div key={occ} className="bg-white px-4 py-3 rounded-full border border-[#B8922A]/40 text-xs uppercase tracking-[0.2em] text-[#5C2018]">
                {occ}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-[#1E7A6A] text-[#FDFAF4]">
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="text-4xl md:text-5xl font-light mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            Visitez-nous au Polygone
          </h2>
          <p className="text-lg font-light mb-8 leading-relaxed">
            Découvrez notre bar à parfums et laissez-vous guider par nos experts en
            parfumerie.
          </p>
          <Link
            to="/rendez-vous"
            className="inline-block bg-[#B8922A] hover:bg-[#9a7922] text-[#FDFAF4] px-8 py-3 rounded-full font-medium uppercase tracking-[0.2em] text-sm transition-colors"
            data-testid="cta-rendezvous"
          >
            Prendre rendez-vous
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
